import { DecisionActionRequest } from "../../models/home";
import { DecisionActionLike } from "./home-decision-action.types";
import { DecisionActionValueReader } from "./home-decision-action-request.builder.input-values";

export type DecisionActionTransportKind =
  | "postpone"
  | "assign"
  | "complete"
  | "discard"
  | "reevaluate"
  | "generic";

const KNOWN_FORM_KEYS = new Set([
  "notes",
  "reason",
  "assignedToUserId",
  "days",
  "snoozedUntil",
  "waitingUntil",
  "blockedReason",
  "missingInformation",
  "completionEvidence",
  "confirmExecution"
]);

export class DecisionActionTransportPayloadBuilder {
  constructor(private readonly valueReader: DecisionActionValueReader) {}

  buildRequest(
    action: DecisionActionLike | null,
    formValue?: Record<string, unknown> | null
  ): DecisionActionRequest | null {
    if (!action?.actionKey) {
      return null;
    }

    const request: DecisionActionRequest = {
      action: action.actionKey,
      endpointTemplate: action.endpointTemplate,
      httpMethod: action.httpMethod,
      payloadTemplate: action.payloadTemplate
    };

    const values = formValue ?? {};
    request.notes = this.valueReader.readStringValue(values, ["notes"]);
    request.reason = this.valueReader.readStringValue(values, ["reason"]);
    request.assignedToUserId = this.valueReader.readStringValue(values, ["assignedToUserId"]);
    request.snoozedUntil = this.valueReader.readStringValue(values, ["snoozedUntil"]);
    request.waitingUntil = this.valueReader.readStringValue(values, ["waitingUntil"]);
    request.blockedReason = this.valueReader.readStringValue(values, ["blockedReason"]);
    request.missingInformation = this.valueReader.readUnknownValue(values, ["missingInformation"]);
    request.completionEvidence = this.valueReader.readUnknownValue(values, ["completionEvidence"]);

    const normalizedActionKey = action.actionKey.toLowerCase();
    const extraValues = Object.entries(values).reduce<Record<string, unknown>>(
      (accumulator, [key, value]) => {
        if (!KNOWN_FORM_KEYS.has(key) && value !== null && value !== undefined && value !== "") {
          accumulator[key] = value;
        }
        return accumulator;
      },
      {}
    );

    if (normalizedActionKey.includes("snooze") && !request.snoozedUntil) {
      request.snoozedUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    }

    if (normalizedActionKey.includes("wait") && !request.waitingUntil) {
      request.waitingUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    }

    if (normalizedActionKey.includes("blocked") && !request.blockedReason) {
      request.blockedReason = this.valueReader.readStringValue(values, ["reason"]);
    }

    if (
      normalizedActionKey.includes("confirm_missing_information")
      && request.missingInformation === undefined
    ) {
      request.missingInformation = Object.keys(extraValues).length ? extraValues : values;
    }

    if (normalizedActionKey === "complete" && request.completionEvidence === undefined) {
      request.completionEvidence = Object.keys(extraValues).length ? extraValues : values;
    }

    request.payload = this.buildTransportPayload(action, request, values, extraValues);
    return this.compactActionRequest(request);
  }

  resolveTransportKind(
    actionKey: string,
    endpointTemplate?: string
  ): DecisionActionTransportKind {
    const endpoint = (endpointTemplate ?? "").trim().toLowerCase();
    if (endpoint.includes("/postpone")) {
      return "postpone";
    }
    if (endpoint.includes("/assign")) {
      return "assign";
    }
    if (endpoint.includes("/complete")) {
      return "complete";
    }
    if (endpoint.includes("/discard")) {
      return "discard";
    }
    if (endpoint.includes("/reevaluate")) {
      return "reevaluate";
    }

    const normalizedActionKey = actionKey.trim().toLowerCase();
    if (
      normalizedActionKey === "postpone"
      || normalizedActionKey.includes("snooze")
      || normalizedActionKey.includes("wait")
    ) {
      return "postpone";
    }
    if (normalizedActionKey === "assign") {
      return "assign";
    }
    if (normalizedActionKey === "complete" || normalizedActionKey.includes("done")) {
      return "complete";
    }
    if (normalizedActionKey === "discard" || normalizedActionKey.includes("dismiss")) {
      return "discard";
    }
    if (
      normalizedActionKey.includes("reevaluate")
      || normalizedActionKey.includes("reclassify")
    ) {
      return "reevaluate";
    }

    return "generic";
  }

  buildTransportPayload(
    action: DecisionActionLike,
    request: DecisionActionRequest,
    values: Record<string, unknown>,
    extraValues: Record<string, unknown>
  ): Record<string, unknown> | undefined {
    const transportKind = this.resolveTransportKind(
      action.actionKey,
      action.endpointTemplate
    );
    const template = this.valueReader.toPayloadRecord(action.payloadTemplate);

    switch (transportKind) {
      case "postpone": {
        const days = this.resolvePostponeDays(values, request, template);
        return this.valueReader.compactPayload({
          ...template,
          days,
          reason: request.reason ?? this.valueReader.readStringValue(template, ["reason"]),
          notes: request.notes ?? this.valueReader.readStringValue(template, ["notes"])
        });
      }
      case "assign":
        return this.valueReader.compactPayload({
          ...template,
          assignedToUserId:
            request.assignedToUserId
            ?? this.valueReader.readStringValue(template, ["assignedToUserId"]),
          reason: request.reason ?? this.valueReader.readStringValue(template, ["reason"]),
          notes: request.notes ?? this.valueReader.readStringValue(template, ["notes"])
        });
      case "complete": {
        const completionEvidence =
          request.completionEvidence
          ?? template["completionEvidence"]
          ?? (Object.keys(extraValues).length ? extraValues : undefined);
        return this.valueReader.compactPayload({
          ...template,
          reason: request.reason ?? this.valueReader.readStringValue(template, ["reason"]),
          notes: request.notes ?? this.valueReader.readStringValue(template, ["notes"]),
          completionEvidence
        });
      }
      case "discard":
        return this.valueReader.compactPayload({
          ...template,
          reason: request.reason ?? this.valueReader.readStringValue(template, ["reason"]),
          notes: request.notes ?? this.valueReader.readStringValue(template, ["notes"])
        });
      case "reevaluate":
        return this.valueReader.compactPayload({
          ...template,
          reason:
            request.reason
            ?? this.valueReader.readStringValue(values, ["reason"])
            ?? this.valueReader.readStringValue(template, ["reason"])
            ?? this.valueReader.readStringValue(values, ["correctionReason"])
            ?? this.valueReader.readStringValue(template, ["correctionReason"]),
          notes: request.notes ?? this.valueReader.readStringValue(template, ["notes"]),
          forceReprocessing:
            this.valueReader.readBooleanValue(values, ["forceReprocessing"])
            ?? this.valueReader.readBooleanValue(template, ["forceReprocessing"]),
          originalArtifactType:
            this.valueReader.readStringValue(values, ["originalArtifactType"])
            ?? this.valueReader.readStringValue(template, ["originalArtifactType"]),
          correctedArtifactType:
            this.valueReader.readStringValue(values, ["correctedArtifactType"])
            ?? this.valueReader.readStringValue(template, ["correctedArtifactType"]),
          userConfidenceScore:
            this.valueReader.readNumberValue(values, ["userConfidenceScore"])
            ?? this.valueReader.readNumberValue(template, ["userConfidenceScore"]),
          correctionReason:
            this.valueReader.readStringValue(values, ["correctionReason"])
            ?? this.valueReader.readStringValue(template, ["correctionReason"]),
          correctionNotes:
            this.valueReader.readStringValue(values, ["correctionNotes"])
            ?? this.valueReader.readStringValue(template, ["correctionNotes"]),
          documentTextSnippet:
            this.valueReader.readStringValue(values, ["documentTextSnippet"])
            ?? this.valueReader.readStringValue(template, ["documentTextSnippet"])
        });
      default:
        return Object.keys(template).length ? template : undefined;
    }
  }

  resolvePostponeDays(
    values: Record<string, unknown>,
    request: DecisionActionRequest,
    template: Record<string, unknown>
  ): number {
    const directDays =
      this.valueReader.readNumberValue(values, ["days"])
      ?? this.valueReader.readNumberValue(template, ["days"]);
    if (directDays !== undefined) {
      return Math.max(1, Math.ceil(directDays));
    }

    const fallbackDate = request.snoozedUntil ?? request.waitingUntil;
    if (fallbackDate) {
      const derivedDays = this.toPostponeDays(fallbackDate);
      if (derivedDays !== undefined) {
        return derivedDays;
      }
    }

    return 1;
  }

  toPostponeDays(value: string): number | undefined {
    const target = new Date(value);
    if (Number.isNaN(target.getTime())) {
      return undefined;
    }

    const deltaMs = target.getTime() - Date.now();
    return Math.max(1, Math.ceil(deltaMs / (24 * 60 * 60 * 1000)));
  }

  compactActionRequest(request: DecisionActionRequest): DecisionActionRequest {
    return Object.entries(request).reduce<DecisionActionRequest>(
      (accumulator, [key, value]) => {
        if (value !== undefined) {
          accumulator[key as keyof DecisionActionRequest] = value as never;
        }
        return accumulator;
      },
      {}
    );
  }
}
